// src/api/services/proposalService.ts
import axiosInstance from "../api.axiosInstance"
import type {
  AdminCreateProposalDataDto,
  AdminMockAddressDto,
  AdminProposalUserOptionDto,
  AdminProposalUserRole,
} from "../dto/admin-proposal.dto"
import type { EnterpriseListItem } from "../dto/enterprise-dto"
import type { ProposalLoanProductPartnersResponseDto } from "../dto/loan-product-partner.dto"
import type {
  ActivateCompletedDesignResponseDto,
  AuroraProjectDesignsDto,
  LinkAuroraDesignResponseDto,
  ProposalDesignDto,
  ProposalDesignsQueryDto,
  RefreshAuroraDesignResponseDto,
  SetAuroraDesignAutoRefreshResponseDto,
} from "../dto/proposal-design.dto"
import type { ProposalLoanProgramDto } from "../dto/proposal-loan-program.dto"
import type {
  ProposalEquipmentPreviewRequestDto,
  ProposalEquipmentPreviewResponseDto,
  UpdateProposalProductOfferingDto,
  UpdateProposalProductOfferingResponseDto,
} from "../dto/proposal-product-offering.dto"
import type {
  AdminDesignInverterDto,
  AdminProposalActionResponseDto,
  AdminProposalChangeOrderEquipmentDto,
  CloneAdminProposalResponseDto,
  ConcertChangeOrderInfoDto,
  EquipmentConfigurationSnapshotDto,
  FetchProposalsInput,
  ProposalDebugRequestBodyDto,
  PowurOsPricingDto,
  ProposalListResponseDto,
  ProposalSavingsDto,
  UpdateEquipmentConfigurationDto,
  UpdateProposalFinanceDto,
  UpdateProposalFinancePlanDto,
} from "../dto/proposal.dto"

const isProposalNotFoundError = (error: unknown): boolean => {
  const { response } = error as { response?: { status?: unknown } }

  return response?.status === 404
}

const debugRequestErrorMessage = (error: unknown): string | undefined => {
  const { response } = error as {
    response?: { data?: { message?: unknown } }
  }
  const message = response?.data?.message

  if (typeof message === `string` && message.trim() !== ``) return message
  if (
    Array.isArray(message) &&
    message.length > 0 &&
    message.every((entry) => typeof entry === `string`)
  ) {
    return message.join(`, `)
  }
  return undefined
}

export class ProposalService {
  async fetchAdminCreateProposalData(): Promise<AdminCreateProposalDataDto> {
    const response = await axiosInstance.get<AdminCreateProposalDataDto>(
      `/proposal/v2/dev/create-data`,
    )
    return response.data
  }

  async fetchAdminCreateProposalUserOptions(
    role: AdminProposalUserRole,
    search: string,
  ): Promise<AdminProposalUserOptionDto[]> {
    const response = await axiosInstance.get<AdminProposalUserOptionDto[]>(
      `/proposal/v2/dev/user-options`,
      { params: { role, search } },
    )
    return response.data
  }

  async fetchAdminCreateProposalEnterpriseOptions(
    search: string,
  ): Promise<EnterpriseListItem[]> {
    const response = await axiosInstance.get<EnterpriseListItem[]>(
      `/proposal/v2/dev/enterprise-options`,
      { params: { search: search || undefined } },
    )
    return response.data
  }

  async fetchAdminMockAddress(state: string): Promise<AdminMockAddressDto> {
    const response = await axiosInstance.get<AdminMockAddressDto>(
      `/proposal/v2/dev/mock-address`,
      { params: { state } },
    )
    return response.data
  }

  async fetchProposals(
    input: FetchProposalsInput,
  ): Promise<ProposalListResponseDto> {
    const response = await axiosInstance.get<ProposalListResponseDto>(
      `/proposal/v2/proposals`,
      {
        params: input,
      },
    )

    return response.data
  }

  async cloneAdminProposal(
    proposalId: string,
  ): Promise<CloneAdminProposalResponseDto> {
    const response = await axiosInstance.post<CloneAdminProposalResponseDto>(
      `/proposal/v2/${proposalId}/clone`,
    )
    return response.data
  }

  async refreshAdminProposal(
    proposalId: string,
  ): Promise<AdminProposalActionResponseDto> {
    const response = await axiosInstance.post<AdminProposalActionResponseDto>(
      `/proposal/v2/${proposalId}/refresh`,
    )
    return response.data
  }

  async fixAdminProposalOptimalEstimate(
    proposalId: string,
  ): Promise<AdminProposalActionResponseDto> {
    const response = await axiosInstance.post<AdminProposalActionResponseDto>(
      `/proposal/v2/${proposalId}/debug/fix-optimal-estimate`,
    )
    return response.data
  }

  async refreshAdminProposalSite(
    proposalId: string,
  ): Promise<AdminProposalActionResponseDto> {
    const response = await axiosInstance.post<AdminProposalActionResponseDto>(
      `/proposal/v2/${proposalId}/debug/refresh-site`,
    )
    return response.data
  }

  async fetchAdminDesignInverters(
    proposalId: string,
  ): Promise<AdminDesignInverterDto[]> {
    const response = await axiosInstance.get<AdminDesignInverterDto[]>(
      `/proposal/v2/${proposalId}/debug/design-inverters`,
    )
    return response.data
  }

  async removeAdminDesignInverter(
    proposalId: string,
    designEquipmentId: string,
  ): Promise<AdminProposalActionResponseDto> {
    const response = await axiosInstance.delete<AdminProposalActionResponseDto>(
      `/proposal/v2/${proposalId}/debug/design-inverters/${designEquipmentId}`,
    )
    return response.data
  }

  async updateAdminProposalChangeOrderEquipment(
    proposalId: string,
    allowChangeOrderEquipment: boolean,
  ): Promise<AdminProposalChangeOrderEquipmentDto> {
    const response =
      await axiosInstance.patch<AdminProposalChangeOrderEquipmentDto>(
        `/proposal/v2/${proposalId}/change-order-equipment`,
        { allowChangeOrderEquipment },
      )
    return response.data
  }

  async fetchProposalDesigns(
    proposalId: string,
    query: ProposalDesignsQueryDto = {},
  ): Promise<ProposalDesignDto[]> {
    try {
      const response = await axiosInstance.get<ProposalDesignDto[]>(
        `/proposal/v1/${proposalId}/designs`,
        {
          params: query,
        },
      )

      return response.data
    } catch (error: unknown) {
      if (isProposalNotFoundError(error)) {
        throw new Error(`Proposal not found`)
      }
      throw error
    }
  }

  async linkAuroraDesign(
    proposalId: string,
    auroraDesignId: string,
  ): Promise<LinkAuroraDesignResponseDto> {
    try {
      const response = await axiosInstance.post<LinkAuroraDesignResponseDto>(
        `/proposal/v1/${proposalId}/aurora-designs`,
        { auroraDesignId },
      )

      return response.data
    } catch (error: unknown) {
      if (isProposalNotFoundError(error)) {
        throw new Error(`Proposal not found`)
      }
      throw error
    }
  }

  async fetchAuroraProjectDesigns(
    proposalId: string,
  ): Promise<AuroraProjectDesignsDto> {
    try {
      const response = await axiosInstance.get<AuroraProjectDesignsDto>(
        `/proposal/v1/${proposalId}/aurora-designs/project-designs`,
      )

      return response.data
    } catch (error: unknown) {
      if (isProposalNotFoundError(error)) {
        throw new Error(`Proposal not found`)
      }
      throw error
    }
  }

  async setAuroraDesignAutoRefresh(
    proposalId: string,
    enabled: boolean,
  ): Promise<SetAuroraDesignAutoRefreshResponseDto> {
    try {
      const response =
        await axiosInstance.patch<SetAuroraDesignAutoRefreshResponseDto>(
          `/proposal/v1/${proposalId}/aurora-designs/active/auto-refresh`,
          { enabled },
        )

      return response.data
    } catch (error: unknown) {
      if (isProposalNotFoundError(error)) {
        throw new Error(`Proposal not found`)
      }
      throw error
    }
  }

  async refreshAuroraDesign(
    proposalId: string,
  ): Promise<RefreshAuroraDesignResponseDto> {
    try {
      const response = await axiosInstance.post<RefreshAuroraDesignResponseDto>(
        `/proposal/v1/${proposalId}/aurora-designs/refresh`,
      )

      return response.data
    } catch (error: unknown) {
      if (isProposalNotFoundError(error)) {
        throw new Error(`Proposal not found`)
      }
      throw error
    }
  }

  async activateCompletedDesign(
    proposalId: string,
  ): Promise<ActivateCompletedDesignResponseDto> {
    try {
      const response =
        await axiosInstance.post<ActivateCompletedDesignResponseDto>(
          `/proposal/v1/${proposalId}/debug/activate-completed-design`,
        )

      return response.data
    } catch (error: unknown) {
      if (isProposalNotFoundError(error)) {
        throw new Error(`Proposal not found`)
      }
      throw error
    }
  }

  async fetchTotalCostByMargin(
    proposalId: string,
    margin: number,
  ): Promise<PowurOsPricingDto> {
    try {
      const response = await axiosInstance.get(
        `/proposal/v1/${proposalId}/pricing/summary`,
        {
          params: { margin },
        },
      )
      return response.data
    } catch (error: unknown) {
      if (isProposalNotFoundError(error)) {
        throw new Error(`Proposal not found`)
      }
      throw error
    }
  }

  async fetchProposalPricingSummaryByTotalCost(
    proposalId: string,
    totalCost: number,
  ): Promise<PowurOsPricingDto> {
    try {
      const response = await axiosInstance.get(
        `/proposal/v1/${proposalId}/pricing/total-cost`,
        {
          params: { totalCost },
        },
      )
      return response.data
    } catch (error: unknown) {
      if (isProposalNotFoundError(error)) {
        throw new Error(`Proposal not found`)
      }
      throw error
    }
  }

  async bustEquipmentCache(proposalId: string): Promise<{ status: boolean }> {
    try {
      const response = await axiosInstance.put(
        `/proposal/v1/${proposalId}/cache/bust/equipment`,
      )
      return response.data
    } catch (error: unknown) {
      if (isProposalNotFoundError(error)) {
        throw new Error(`Proposal not found`)
      }
      throw error
    }
  }

  async fetchProposalPricing(proposalId: string): Promise<PowurOsPricingDto> {
    try {
      const response = await axiosInstance.get(
        `/proposal/v1/${proposalId}/pricing`,
      )
      return response.data
    } catch (error: unknown) {
      if (isProposalNotFoundError(error)) {
        throw new Error(`Proposal not found`)
      }
      throw error
    }
  }

  async fetchProposalPricingComparisons(
    proposalId: string,
    requestedMargins: number[],
  ): Promise<PowurOsPricingDto[]> {
    try {
      const response = await axiosInstance.post(
        `/proposal/v1/${proposalId}/pricing/comparisons`,
        {
          requestedMargins,
        },
      )
      return response.data
    } catch (error: any) {
      if (error.response?.status === 404) {
        throw new Error(`Proposal not found`)
      }
      throw error
    }
  }

  async updateProposalFinancePlan(
    proposalId: string,
    body: UpdateProposalFinancePlanDto,
  ): Promise<ProposalLoanProgramDto[]> {
    try {
      const response = await axiosInstance.put(
        `/proposal/v1/${proposalId}/finance/plan`,
        body,
      )
      return response.data
    } catch (error: any) {
      if (error.response?.status === 404) {
        throw new Error(`Proposal not found`)
      }
      throw error
    }
  }

  async fetchProposalLoanProductPartners(
    proposalId: string,
  ): Promise<ProposalLoanProductPartnersResponseDto> {
    try {
      const response =
        await axiosInstance.get<ProposalLoanProductPartnersResponseDto>(
          `/proposal/v1/${proposalId}/loan-products/partners`,
        )
      return response.data
    } catch (error: any) {
      if (error.response?.status === 404) {
        throw new Error(`Proposal not found`)
      }
      throw error
    }
  }

  async updateProposalFinanceProduct(
    proposalId: string,
    body: UpdateProposalFinanceDto,
  ): Promise<void> {
    try {
      const response = await axiosInstance.put(
        `/proposal/v1/${proposalId}/finance`,
        body,
      )
      return response.data
    } catch (error: any) {
      if (error.response?.status === 404) {
        throw new Error(`Proposal not found`)
      }
      throw error
    }
  }

  async previewProposalEquipment(
    proposalId: string,
    body: ProposalEquipmentPreviewRequestDto,
    signal?: AbortSignal,
  ): Promise<ProposalEquipmentPreviewResponseDto> {
    try {
      const response =
        await axiosInstance.post<ProposalEquipmentPreviewResponseDto>(
          `/proposal/v1/${proposalId}/equipment`,
          body,
          { signal },
        )
      return response.data
    } catch (error: any) {
      if (error.response?.status === 404) {
        throw new Error(`Proposal not found`)
      }
      throw error
    }
  }

  async updateProposalProductOffering(
    proposalId: string,
    body: UpdateProposalProductOfferingDto,
  ): Promise<UpdateProposalProductOfferingResponseDto> {
    try {
      const response =
        await axiosInstance.put<UpdateProposalProductOfferingResponseDto>(
          `/proposal/v1/${proposalId}/product-offering`,
          body,
        )
      return response.data
    } catch (error: any) {
      if (error.response?.status === 404) {
        throw new Error(`Proposal not found`)
      }
      throw error
    }
  }

  async updateEquipmentConfiguration(
    proposalId: string,
    body: UpdateEquipmentConfigurationDto,
  ): Promise<EquipmentConfigurationSnapshotDto> {
    try {
      const response = await axiosInstance.put(
        `/proposal/v1/${proposalId}/equipment/configuration`,
        body,
      )
      return response.data
    } catch (error: any) {
      if (error.response?.status === 404) {
        throw new Error(`Proposal not found`)
      }
      throw error
    }
  }

  async fetchProposalSavings(proposalId: string): Promise<ProposalSavingsDto> {
    try {
      const response = await axiosInstance.get(
        `/proposal/v1/${proposalId}/savings`,
      )
      return response.data
    } catch (error: any) {
      if (error.response?.status === 404) {
        throw new Error(`Proposal not found`)
      }
      throw error
    }
  }

  async fetchConcertChangeOrderInfo(
    proposalId: string,
  ): Promise<ConcertChangeOrderInfoDto> {
    try {
      const response = await axiosInstance.get<ConcertChangeOrderInfoDto>(
        `/proposal/v1/${proposalId}/concert/change-order-info`,
      )
      return response.data
    } catch (error: any) {
      if (error.response?.status === 404) {
        throw new Error(`Proposal not found`)
      }
      throw error
    }
  }

  async fetchProposalDebugPricingInput(
    proposalId: string,
    signal?: AbortSignal,
  ): Promise<ProposalDebugRequestBodyDto> {
    try {
      const url = `/proposal/v1/${proposalId}/debug/pricing-input`
      const response = signal
        ? await axiosInstance.get<ProposalDebugRequestBodyDto>(url, { signal })
        : await axiosInstance.get<ProposalDebugRequestBodyDto>(url)
      return response.data
    } catch (error: unknown) {
      const serverMessage = debugRequestErrorMessage(error)
      if (serverMessage !== undefined) {
        throw new Error(serverMessage)
      }
      if (isProposalNotFoundError(error)) {
        throw new Error(`Proposal not found`)
      }
      throw error
    }
  }

  async fetchProposalDebugDetailInput(
    proposalId: string,
    signal?: AbortSignal,
  ): Promise<ProposalDebugRequestBodyDto> {
    try {
      const url = `/proposal/v1/${proposalId}/debug/detail-input`
      const response = signal
        ? await axiosInstance.get<ProposalDebugRequestBodyDto>(url, { signal })
        : await axiosInstance.get<ProposalDebugRequestBodyDto>(url)
      return response.data
    } catch (error: unknown) {
      const serverMessage = debugRequestErrorMessage(error)
      if (serverMessage !== undefined) {
        throw new Error(serverMessage)
      }
      if (isProposalNotFoundError(error)) {
        throw new Error(`Proposal not found`)
      }
      throw error
    }
  }
}

export const proposalService = new ProposalService()
